import React, { useCallback, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CloudUpload } from '@mui/icons-material';
import PropTypes from 'prop-types';

const useStyles = makeStyles(() => ({
  dropZone: {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: '#3C55E8',
    },
    '&.dragover': {
      borderColor: '#3C55E8',
      backgroundColor: '#f5f5f5',
    },
  },
  uploadIcon: {
    fontSize: '48px',
    color: '#666',
    marginBottom: '16px',
  },
  uploadText: {
    marginBottom: '16px',
    color: '#666',
  },
  fileInput: {
    display: 'none',
  },
}));

export const DropZoneImage = ({
  useGallery,
  actionCallback,
  onUploadStart,
  onUploadComplete,
  uploadingFile,
  onFileSelected,
  handleEdit,
}) => {
  const classes = useStyles();
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(
    (file) => {
      if (!file || !file.type.startsWith('image/')) {
        actionCallback({
          type: 'upload',
          status: 'error',
          payload: { message: 'Please select an image file' },
        });
        return;
      }

      // Create a temporary file object for editing
      const fileForEdit = {
        file: file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        mime: file.type,
        id: `temp-${Date.now()}`,
      };

      // Trigger edit mode instead of direct upload
      const editHandler = handleEdit || onFileSelected;
      editHandler(fileForEdit);
    },
    [actionCallback, onFileSelected, handleEdit]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
      e.target.value = ''; // Reset input
    },
    [handleFile]
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload New Image
      </Typography>
      <Box
        className={`${classes.dropZone} ${dragOver ? 'dragover' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById('file-input').click()}
      >
        <CloudUpload className={classes.uploadIcon} />
        <Typography className={classes.uploadText}>
          {uploadingFile
            ? `Uploading ${uploadingFile.name}...`
            : 'Drag and drop an image here, or click to select'}
        </Typography>
        <Button variant="outlined" disabled={!!uploadingFile}>
          Choose File
        </Button>
        <input
          id="file-input"
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className={classes.fileInput}
        />
      </Box>
    </Box>
  );
};

DropZoneImage.propTypes = {
  useGallery: PropTypes.func.isRequired,
  actionCallback: PropTypes.func.isRequired,
  onUploadStart: PropTypes.func.isRequired,
  onUploadComplete: PropTypes.func.isRequired,
  uploadingFile: PropTypes.object,
  onFileSelected: PropTypes.func.isRequired,
};

export default DropZoneImage;
